import React, { useMemo, type ReactNode } from "react";
import {
  useFieldArray,
  useWatch,
  type Control,
  type FieldErrors,
  type FieldValues,
} from "react-hook-form";
import "./DynamicMappingField.scss";
import DynamicMappingFieldRow from "./DynamicMappingFieldRow/DynamicMappingFieldRow";

interface DynamicMappingFieldProps {
  control: Control<FieldValues>;
  formState: { errors: FieldErrors<FieldValues> };
  // Built from `mapping.rows.map((row) => row.field_from)` in GenericField; the
  // `field_from` shape is not modeled here, so each option is left as `unknown`.
  options?: unknown[];
  registerPrefix: string;
  label?: ReactNode;
  description?: ReactNode;
  value?: unknown;
  // The full GenericField prop set is spread onto each row, so arbitrary
  // passthrough props are accepted here.
  [key: string]: unknown;
}

const DynamicMappingField: React.FC<DynamicMappingFieldProps> = (props) => {
  const {
    control,
    formState: { errors },
    options,
    registerPrefix,
    label,
    description,
  } = props;

  const { fields, update, append, remove } = useFieldArray({
    control,
    name: registerPrefix,
  });

  const watchFieldArray = useWatch({
    control,
    name: registerPrefix,
  });

  const mappedfields = () => {
    const mappedFields = controlledFields
      .filter((el) => !!el?.field)
      .map((el) => el?.field);
    return mappedFields;
  };

  const controlledFields = useMemo(() => {
    if (!!fields && !!watchFieldArray) {
      return fields.map((field, index) => ({
        ...field,
        ...watchFieldArray[index],
      }));
    }

    return [];
  }, [fields, watchFieldArray]);

  return (
    <div className={"dynamic-mapping-field"}>
      <div className="input-wrap has-no-margin title no-padding-bottom">
        <div className="input-label">{label}</div>
      </div>
      {!!description && (
        <div className="mapping-field-description">{description}</div>
      )}
      <ul>
        {!!controlledFields?.length &&
          controlledFields?.map((field, index) => {
            return (
              <li key={field.id}>
                <DynamicMappingFieldRow
                  {...props}
                  {...field}
                  mappingOptions={options}
                  registerPrefix={registerPrefix}
                  label={label}
                  index={index}
                  updateRow={update}
                  control={control}
                  errors={errors}
                  removeRow={remove}
                  appendRow={append}
                  rowsNumber={controlledFields.length}
                  mappedFields={mappedfields()}
                />
              </li>
            );
          })}
      </ul>
    </div>
  );
};
export default DynamicMappingField;
